import React, { useEffect, useState, useCallback } from 'react';
import Navbar from '../components/Navbar';
import api, { formatRupiah, resolveFileUrl } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Bank, Users, Coins, ChartBar, Check, X, Shield, ShieldCheck,
  MagnifyingGlass, Broom, Warning, ReceiptX, Trash, Key, EnvelopeSimple, Percent, Link
} from '@phosphor-icons/react';

// ---- Withdrawals tab ----
function WithdrawalsTab() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    try {
      const w = await api.get('/admin/withdrawals', { params: filter ? { status: filter } : {} });
      setRows(w.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Gagal memuat'); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const act = async (wid, action) => {
    if (!window.confirm(`Yakin ${action === 'approve' ? 'menyetujui' : 'menolak'} withdrawal ini?`)) return;
    setBusy({ ...busy, [wid]: true });
    try {
      await api.post(`/admin/withdrawals/${wid}/${action}`);
      toast.success(action === 'approve' ? 'Disetujui' : 'Ditolak (saldo direfund)');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal');
    } finally { setBusy({ ...busy, [wid]: false }); }
  };

  return (
    <div className="border-2 border-foreground bg-white shadow-brutal-md overflow-hidden" data-testid="admin-tab-withdrawals">
      <div className="p-5 border-b-2 border-foreground flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl font-bold">Pengajuan Penarikan</h2>
        <div className="flex gap-1">
          {['pending', 'approved', 'rejected', ''].map((s) => (
            <button key={s || 'all'} onClick={() => setFilter(s)}
              className={`px-3 py-1.5 text-xs font-bold uppercase border-2 transition-all ${
                filter === s ? 'border-foreground bg-primary text-white' : 'border-transparent hover:bg-muted hover:border-foreground'
              }`}
              data-testid={`admin-filter-${s || 'all'}`}>
              {s || 'Semua'}
            </button>
          ))}
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="p-10 text-center text-muted-foreground">Tidak ada data.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background">
              <tr>
                <th className="text-left p-3 font-bold uppercase text-xs">Kreator</th>
                <th className="text-right p-3 font-bold uppercase text-xs">Jumlah Kotor</th>
                <th className="text-right p-3 font-bold uppercase text-xs">Biaya (0.8%)</th>
                <th className="text-right p-3 font-bold uppercase text-xs">Jumlah Bersih</th>
                <th className="text-left p-3 font-bold uppercase text-xs">Rekening</th>
                <th className="text-left p-3 font-bold uppercase text-xs">Status</th>
                <th className="text-left p-3 font-bold uppercase text-xs">Waktu</th>
                <th className="text-right p-3 font-bold uppercase text-xs">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((w) => {
                const fee = w.fee !== undefined ? w.fee : Math.round(w.amount * 0.008);
                const net = w.net_amount !== undefined ? w.net_amount : (w.amount - fee);
                return (
                  <tr key={w.withdrawal_id} className="border-t-2 border-foreground" data-testid={`admin-wd-${w.withdrawal_id}`}>
                    <td className="p-3"><p className="font-bold">{w.user_name}</p><p className="text-xs text-muted-foreground">{w.user_email}</p></td>
                    <td className="p-3 text-right font-bold">{formatRupiah(w.amount)}</td>
                    <td className="p-3 text-right text-muted-foreground">{formatRupiah(fee)}</td>
                    <td className="p-3 text-right font-bold text-secondary">{formatRupiah(net)}</td>
                    <td className="p-3 text-xs"><p className="font-bold">{w.bank_name}</p><p className="font-mono">{w.account_number}</p><p className="text-muted-foreground">{w.account_holder}</p></td>
                    <td className="p-3">
                      <span className={`inline-block border-2 border-foreground px-2 py-0.5 text-xs font-bold uppercase ${
                        w.status === 'pending' ? 'bg-accent' : w.status === 'approved' ? 'bg-secondary' : 'bg-destructive text-white'
                      }`}>{w.status}</span>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">{new Date(w.created_at).toLocaleString('id-ID')}</td>
                    <td className="p-3 text-right">
                      {w.status === 'pending' ? (
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => act(w.withdrawal_id, 'approve')} disabled={busy[w.withdrawal_id]}
                            className="inline-flex items-center gap-1 border-2 border-foreground bg-secondary px-2 py-1 text-xs font-bold uppercase shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px] disabled:opacity-60"
                            data-testid={`admin-approve-${w.withdrawal_id}`}>
                            <Check size={12} weight="bold" /> Approve
                          </button>
                          <button onClick={() => act(w.withdrawal_id, 'reject')} disabled={busy[w.withdrawal_id]}
                            className="inline-flex items-center gap-1 border-2 border-foreground bg-destructive text-white px-2 py-1 text-xs font-bold uppercase shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px] disabled:opacity-60"
                            data-testid={`admin-reject-${w.withdrawal_id}`}>
                            <X size={12} weight="bold" /> Reject
                          </button>
                        </div>
                      ) : (<span className="text-xs text-muted-foreground">—</span>)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Users tab ----
function UsersTab({ currentAdmin, defaultFeePercent }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState({});
  const [loading, setLoading] = useState(true);

  const changeCustomFee = async (user) => {
    const currentFee = user.custom_fee_percent !== undefined && user.custom_fee_percent !== null ? user.custom_fee_percent : '';
    const newFeeStr = window.prompt(`Masukkan custom fee (%) untuk ${user.email} (kosongkan untuk kembali ke default):`, currentFee);
    if (newFeeStr === null) return;
    
    let feeVal = null;
    if (newFeeStr.trim() !== '') {
      feeVal = parseFloat(newFeeStr);
      if (isNaN(feeVal) || feeVal < 0 || feeVal > 100) {
        toast.error('Masukkan angka yang valid antara 0 dan 100');
        return;
      }
    }
    
    setBusy({ ...busy, [user.user_id]: true });
    try {
      await api.post(`/admin/users/${user.user_id}/custom-fee`, null, { params: { fee_percent: feeVal } });
      toast.success('Custom fee berhasil diperbarui');
      load(q);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mengubah custom fee');
    } finally {
      setBusy({ ...busy, [user.user_id]: false });
    }
  };

  const load = useCallback(async (search) => {
    setLoading(true);
    try {
      const res = await api.get('/admin/users', { params: { q: search || undefined, limit: 50 } });
      setRows(res.data);
    } catch (err) {
      toast.error('Gagal memuat user');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleAdmin = async (user, value) => {
    const label = value ? 'menjadikan' : 'mencabut';
    if (!window.confirm(`Yakin ${label} ${user.email} sebagai admin?`)) return;
    setBusy({ ...busy, [user.user_id]: true });
    // Optimistic update so the Grant/Revoke button flips immediately
    setRows((curr) => curr.map((u) => u.user_id === user.user_id ? { ...u, is_admin: value } : u));
    try {
      await api.post(`/admin/users/${user.user_id}/set-admin`, null, { params: { value } });
      toast.success(value ? 'Admin ditambahkan' : 'Admin dicabut');
      await load(q);
    } catch (err) {
      // Revert optimistic change on failure
      setRows((curr) => curr.map((u) => u.user_id === user.user_id ? { ...u, is_admin: !value } : u));
      toast.error(err.response?.data?.detail || 'Gagal');
    } finally { setBusy({ ...busy, [user.user_id]: false }); }
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`PERINGATAN: Yakin ingin MENGHAPUS TOTAL user ${user.email}? Semua data (transaksi, unit, goal) akan hilang.`)) return;
    setBusy({ ...busy, [user.user_id]: true });
    try {
      await api.delete(`/admin/users/${user.user_id}`);
      toast.success('User berhasil dihapus');
      load(q);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menghapus user');
    } finally { setBusy({ ...busy, [user.user_id]: false }); }
  };

  const changePassword = async (user) => {
    const newPass = window.prompt(`Masukkan password baru untuk ${user.email}:`, "");
    if (!newPass) return;
    if (newPass.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }
    setBusy({ ...busy, [user.user_id]: true });
    try {
      await api.post(`/admin/users/${user.user_id}/password`, { password: newPass });
      toast.success('Password berhasil diubah');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mengubah password');
    } finally { setBusy({ ...busy, [user.user_id]: false }); }
  };

  const verifyEmail = async (user) => {
    if (!window.confirm(`Verifikasi email ${user.email} secara manual?`)) return;
    setBusy({ ...busy, [user.user_id]: true });
    try {
      await api.post(`/admin/users/${user.user_id}/verify-email`);
      toast.success('Email berhasil diverifikasi');
      load(q);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal verifikasi');
    } finally { setBusy({ ...busy, [user.user_id]: false }); }
  };

  return (
    <div className="border-2 border-foreground bg-white shadow-brutal-md overflow-hidden" data-testid="admin-tab-users">
      <div className="p-5 border-b-2 border-foreground flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl font-bold">Manajemen User & Admin</h2>
        <div className="relative">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load(q); }}
            placeholder="Cari email/nama/username"
            className="border-2 border-foreground bg-white pl-7 pr-3 py-1.5 text-sm shadow-brutal-sm focus:outline-none focus:border-primary"
            data-testid="admin-users-search"
          />
        </div>
      </div>
      {loading ? (
        <div className="p-10 text-center">Memuat...</div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-muted-foreground">Tidak ada user.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background">
              <tr>
                <th className="text-left p-3 font-bold uppercase text-xs">User</th>
                <th className="text-left p-3 font-bold uppercase text-xs">Status Email</th>
                <th className="text-left p-3 font-bold uppercase text-xs">Saldo</th>
                <th className="text-left p-3 font-bold uppercase text-xs">Fee %</th>
                <th className="text-left p-3 font-bold uppercase text-xs">Auth</th>
                <th className="text-left p-3 font-bold uppercase text-xs">Role</th>
                <th className="text-right p-3 font-bold uppercase text-xs">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.user_id} className="border-t-2 border-foreground" data-testid={`admin-user-${u.user_id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {u.picture ? (
                        <img src={resolveFileUrl(u.picture)} alt="" className="w-8 h-8 border-2 border-foreground object-cover" />
                      ) : (
                        <div className="w-8 h-8 border-2 border-foreground bg-muted" />
                      )}
                      <div className="min-w-0">
                        <p className="font-bold truncate">{u.name}</p>
                        <p className="text-xs text-muted-foreground truncate italic">@{u.username}</p>
                        <p className="text-[10px] font-mono text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    {u.email_verified ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-secondary">
                        <Check size={10} weight="bold" /> Terverifikasi
                      </span>
                    ) : (
                      <div className="flex flex-col gap-1">
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-destructive">
                          <X size={10} weight="bold" /> Belum
                        </span>
                        <button onClick={() => verifyEmail(u)} className="text-[9px] font-bold underline text-left hover:text-primary">
                          Verifikasi Manual
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="p-3 font-bold text-xs">{formatRupiah(u.balance)}</td>
                  <td className="p-3 text-xs font-bold text-primary">
                    {u.custom_fee_percent !== undefined && u.custom_fee_percent !== null ? `${u.custom_fee_percent}%` : `Default (${defaultFeePercent || 3}%)`}
                  </td>
                  <td className="p-3 text-xs uppercase">{u.auth_provider}</td>
                  <td className="p-3">
                    {u.is_admin ? (
                      <span className="inline-flex items-center gap-1 border-2 border-foreground bg-foreground text-background px-2 py-0.5 text-[10px] font-bold uppercase">
                        <ShieldCheck size={10} weight="bold" /> Admin
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 border-2 border-foreground bg-muted px-2 py-0.5 text-[10px] font-bold uppercase">User</span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex flex-wrap gap-1 justify-end">
                      <button onClick={() => changePassword(u)} disabled={busy[u.user_id] || u.auth_provider === 'google'}
                        className="p-1.5 border-2 border-foreground bg-white shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px] disabled:opacity-40"
                        title="Ganti Password">
                        <Key size={12} weight="bold" />
                      </button>

                      <button onClick={() => changeCustomFee(u)} disabled={busy[u.user_id]}
                        className="p-1.5 border-2 border-foreground bg-white shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px] disabled:opacity-40"
                        title="Atur Custom Fee">
                        <Percent size={12} weight="bold" />
                      </button>
                      
                      {u.is_admin ? (
                        <button onClick={() => toggleAdmin(u, false)} disabled={busy[u.user_id] || u.user_id === currentAdmin?.user_id}
                          className="p-1.5 border-2 border-foreground bg-foreground text-background shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px] disabled:opacity-40"
                          title="Revoke Admin">
                          <Shield size={12} weight="bold" />
                        </button>
                      ) : (
                        <button onClick={() => toggleAdmin(u, true)} disabled={busy[u.user_id]}
                          className="p-1.5 border-2 border-foreground bg-secondary shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px] disabled:opacity-40"
                          title="Jadikan Admin">
                          <ShieldCheck size={12} weight="bold" />
                        </button>
                      )}
                      <button onClick={() => deleteUser(u)} disabled={busy[u.user_id] || u.user_id === currentAdmin?.user_id}
                        className="p-1.5 border-2 border-foreground bg-destructive text-white shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px] disabled:opacity-40"
                        title="Hapus User">
                        <Trash size={12} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Platform Config tab ----
function PlatformConfigTab() {
  const [config, setConfig] = useState({
    discord_url: '',
    whatsapp_url: '',
    instagram_url: '',
    facebook_url: '',
    youtube_url: '',
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await api.get('/platform/config');
        if (res.data) setConfig(res.data);
      } catch (err) {
        toast.error('Gagal memuat konfigurasi platform');
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/platform/config', config);
      toast.success('Pengaturan platform berhasil disimpan!');
    } catch (err) {
      toast.error('Gagal menyimpan pengaturan');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => setConfig({ ...config, [e.target.name]: e.target.value });

  if (loading) return <div className="p-10 text-center">Memuat pengaturan...</div>;

  return (
    <div className="border-2 border-foreground bg-white shadow-brutal-md p-5 md:p-6 space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Link size={24} weight="duotone" />
        <h2 className="font-display text-xl font-bold">Pengaturan Link Platform</h2>
      </div>
      
      <form onSubmit={handleSave} className="space-y-4 max-w-xl">
        <div>
          <label className="text-xs uppercase tracking-widest font-bold block mb-1">URL Discord (Komunitas)</label>
          <input type="url" name="discord_url" value={config.discord_url} onChange={handleChange} placeholder="https://discord.gg/..."
            className="w-full border-2 border-foreground bg-white px-3 py-2 shadow-brutal-sm focus:outline-none focus:border-primary" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold block mb-1">URL Bantuan Cepat / WhatsApp</label>
          <input type="url" name="whatsapp_url" value={config.whatsapp_url} onChange={handleChange} placeholder="https://wa.me/..."
            className="w-full border-2 border-foreground bg-white px-3 py-2 shadow-brutal-sm focus:outline-none focus:border-primary" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold block mb-1">URL Instagram</label>
          <input type="url" name="instagram_url" value={config.instagram_url} onChange={handleChange} placeholder="https://instagram.com/..."
            className="w-full border-2 border-foreground bg-white px-3 py-2 shadow-brutal-sm focus:outline-none focus:border-primary" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold block mb-1">URL Facebook</label>
          <input type="url" name="facebook_url" value={config.facebook_url} onChange={handleChange} placeholder="https://facebook.com/..."
            className="w-full border-2 border-foreground bg-white px-3 py-2 shadow-brutal-sm focus:outline-none focus:border-primary" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold block mb-1">URL YouTube</label>
          <input type="url" name="youtube_url" value={config.youtube_url} onChange={handleChange} placeholder="https://youtube.com/..."
            className="w-full border-2 border-foreground bg-white px-3 py-2 shadow-brutal-sm focus:outline-none focus:border-primary" />
        </div>
        
        <button type="submit" disabled={saving}
          className="mt-4 inline-flex items-center gap-2 border-2 border-foreground bg-primary text-white px-6 py-2 font-bold uppercase text-sm tracking-wide shadow-brutal-sm hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] disabled:opacity-60">
          <Check size={16} weight="bold" /> {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
        </button>
      </form>
    </div>
  );
}

// ---- Maintenance tab (storage cleanup) ----
function MaintenanceTab({ onCleaned }) {
  const [days, setDays] = useState(7);
  const [dryRun, setDryRun] = useState(null);
  const [running, setRunning] = useState(false);

  const check = async () => {
    setRunning(true);
    try {
      const res = await api.post('/admin/storage/cleanup', null, { params: { older_than_days: days, dry_run: true } });
      setDryRun(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Gagal'); }
    finally { setRunning(false); }
  };

  const purge = async () => {
    if (!window.confirm(`Yakin purge ${dryRun?.would_purge || 0} record file yang soft-deleted > ${days} hari?`)) return;
    setRunning(true);
    try {
      const res = await api.post('/admin/storage/cleanup', null, { params: { older_than_days: days, dry_run: false } });
      toast.success(`Dihapus ${res.data.purged} record`);
      setDryRun(null);
      onCleaned?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Gagal'); }
    finally { setRunning(false); }
  };

  return (
    <div className="border-2 border-foreground bg-white shadow-brutal-md p-5 md:p-6 space-y-4" data-testid="admin-tab-maintenance">
      <div className="flex items-center gap-2">
        <Broom size={24} weight="duotone" />
        <h2 className="font-display text-xl font-bold">Storage Cleanup</h2>
      </div>
      <div className="border-2 border-foreground bg-background p-3 text-xs flex gap-2">
        <Warning size={14} weight="bold" className="mt-0.5 shrink-0" />
        <p>Emergent Object Storage tidak mendukung delete. Cleanup ini hanya purge <strong>record DB</strong> untuk file yang sudah soft-deleted — physical object tetap ada di bucket.</p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs uppercase tracking-widest font-bold block mb-1">Lebih Tua Dari (hari)</label>
          <input type="number" min="0" max="365" value={days} onChange={(e) => setDays(parseInt(e.target.value || '0'))}
            className="w-32 border-2 border-foreground bg-white px-3 py-2 shadow-brutal-sm focus:outline-none focus:border-primary"
            data-testid="cleanup-days-input" />
        </div>
        <button onClick={check} disabled={running}
          className="inline-flex items-center gap-2 border-2 border-foreground bg-accent px-4 py-2 font-bold uppercase text-xs tracking-wide shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px] disabled:opacity-60"
          data-testid="cleanup-check-btn">
          <ReceiptX size={14} weight="bold" /> Cek (Dry Run)
        </button>
        <button onClick={purge} disabled={running || !dryRun || dryRun.would_purge === 0}
          className="inline-flex items-center gap-2 border-2 border-foreground bg-destructive text-white px-4 py-2 font-bold uppercase text-xs tracking-wide shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px] disabled:opacity-40"
          data-testid="cleanup-purge-btn">
          <Broom size={14} weight="bold" /> Purge
        </button>
      </div>
      {dryRun && (
        <div className="border-2 border-foreground bg-secondary p-4" data-testid="cleanup-result">
          <p className="text-sm"><strong>{dryRun.would_purge}</strong> record akan di-purge (lebih tua dari {days} hari).</p>
        </div>
      )}
    </div>
  );
}

// ---- Main page ----
export default function Admin() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('withdrawals');

  const loadStats = useCallback(async () => {
    try {
      const s = await api.get('/admin/stats');
      setStats(s.data);
    } catch {/* ignore */}
  }, []);
  useEffect(() => { if (user?.is_admin) loadStats(); }, [user, loadStats]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  const statCards = [
    { key: 'users', label: 'Total User', value: (stats?.total_users || 0).toLocaleString('id-ID'), icon: Users, bg: 'bg-accent' },
    { key: 'gmv', label: 'Total GMV', value: formatRupiah(stats?.total_gmv || 0), icon: Coins, bg: 'bg-secondary' },
    { key: 'fee', label: `Revenue (Fee ${stats?.platform_fee_percent || 0}%)`, value: formatRupiah(stats?.total_platform_fee || 0), icon: ChartBar, bg: 'bg-primary text-white' },
    { key: 'tx', label: 'Total Transaksi', value: (stats?.total_completed_tx || 0).toLocaleString('id-ID'), icon: Coins, bg: 'bg-white' },
    { key: 'pending', label: 'WD Pending', value: (stats?.pending_withdrawals || 0).toLocaleString('id-ID'), icon: Bank, bg: 'bg-white' },
    { key: 'admins', label: 'Total Admin', value: (stats?.total_admins || 0).toLocaleString('id-ID'), icon: ShieldCheck, bg: 'bg-foreground text-background' },
  ];

  const tabs = [
    { id: 'withdrawals', label: 'Penarikan', icon: Bank },
    { id: 'users', label: 'User & Admin', icon: Users },
    { id: 'platform', label: 'Link Dinamis', icon: Link },
    { id: 'maintenance', label: 'Maintenance', icon: Broom },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-10 space-y-6" data-testid="admin-page">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] font-bold mb-1">Admin Panel</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Manajemen Platform</h1>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {statCards.map((c) => (
            <div key={c.key} className={`border-2 border-foreground ${c.bg} p-4 shadow-brutal-sm`} data-testid={`admin-stat-${c.key}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-widest font-bold opacity-80">{c.label}</p>
                <c.icon size={18} weight="duotone" />
              </div>
              <p className="font-display text-xl lg:text-2xl font-bold leading-none">{c.value}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-1 border-b-2 border-foreground overflow-x-auto">
          {tabs.map((t) => {
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wide border-2 border-b-0 whitespace-nowrap -mb-0.5 transition-all ${
                  active ? 'border-foreground bg-primary text-white' : 'border-transparent hover:bg-muted'
                }`}
                data-testid={`admin-tab-${t.id}`}>
                <t.icon size={14} weight="bold" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'withdrawals' && <WithdrawalsTab />}
        {tab === 'users' && <UsersTab currentAdmin={user} defaultFeePercent={stats?.platform_fee_percent} />}
        {tab === 'platform' && <PlatformConfigTab />}
        {tab === 'maintenance' && <MaintenanceTab onCleaned={loadStats} />}
      </main>
    </div>
  );
}
